<template>
  <v-app>
    <v-main class="main">

      <!-- Sidebar -->
      <AdminSidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
        @update:drawerOpen="drawerOpen = $event" />

      <v-container fluid class="main-content pa-4">

        <!-- Header -->
        <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar" />

        <!-- Table Card -->
        <v-card class="table-card mt-6">

          <!-- Header -->
          <div class="table-header d-flex justify-space-between align-center flex-wrap ga-3">

            <div class="d-flex align-center flex-wrap ga-3">

              <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 8px;">
                <InputText v-model="filters['global'].value" placeholder="Search Campaign..." />

                <Button type="button" icon="pi pi-filter-slash" label="Clear" variant="outlined"
                  @click="clearFilters" />
              </div>

            </div>

            <button type="button" :disabled="campaignLoading" class="theme-button px-5" @click="
              resetForm();
            isEditing = false;
            editingId = null;
            campaignDialog = true;
            ">
              <v-icon start>
                mdi-plus
              </v-icon>

              Create Campaign
            </button>

          </div>


          <!-- Campaign Table -->
          <DataTable v-model:filters="filters" :value="campaigns" paginator :rows="10" dataKey="id" stripedRows
            class="modern-table" :rowsPerPageOptions="[5, 10, 20, 50]" tableStyle="min-width: 70rem"
            filterDisplay="menu" selectionMode="single" :globalFilterFields="[
              'name',
              'type',
              'reward_type'
            ]">

            <!-- Empty -->
            <template #empty>
              No Campaign found.
            </template>

            <!-- Loading -->
            <template #loading>
              Loading Campaigns...
            </template>


            <!-- Campaign Name -->
            <Column header="Campaign">

              <template #body="slotProps">

                <div class="campaign-cell">

                  <div class="campaign-image-wrapper">

                    <img v-if="slotProps.data.image" :src="slotProps.data.image" class="campaign-image" />

                    <div v-else class="campaign-image-placeholder">
                      <v-icon>
                        mdi-bullhorn-outline
                      </v-icon>
                    </div>

                  </div>

                  <div>

                    <div class="campaign-name">
                      {{ slotProps.data.name }}
                    </div>

                    <div class="campaign-description">
                      {{ slotProps.data.description || 'No description' }}
                    </div>

                  </div>

                </div>

              </template>

            </Column>


            <!-- Type -->
            <Column header="Type">

              <template #body="slotProps">

                <span class="type-pill">
                  {{ formatCampaignType(slotProps.data.type) }}
                </span>

              </template>

            </Column>

            <!-- Type -->
            <Column header="Reward Type">

              <template #body="slotProps">

                <span class="type-pill">
                  {{ formatCampaignType(slotProps.data.reward_type) }}
                </span>

              </template>

            </Column>


            <!-- Reward -->
            <Column header="Reward">

              <template #body="slotProps">

                <div v-if="slotProps.data.reward_type === 'fixed'">

                  ₦{{ formatAmount(slotProps.data.reward_amount) }}

                </div>

                <div v-else>

                  {{ slotProps.data.reward_percentage }}%

                </div>

              </template>

            </Column>


            <!-- Referrals -->
            <Column header="Referrals">

              <template #body="slotProps">

                <span class="referral-count">
                  {{ slotProps.data.referrals_count ?? 0 }}
                </span>

              </template>

            </Column>


            <!-- Start -->
            <Column header="Start">

              <template #body="slotProps">

                {{ formatDate(slotProps.data.starts_at) }}

              </template>

            </Column>


            <!-- End -->
            <Column header="End">

              <template #body="slotProps">

                {{ formatDate(slotProps.data.ends_at) }}

              </template>

            </Column>


            <!-- STATUS -->
            <Column header="Status" style="width: 150px">

              <template #body="slotProps">

                <div class="status-toggle">

                  <v-switch v-model="slotProps.data.is_active" color="success" density="compact" hide-details
                    :loading="statusLoadingId === slotProps.data.id" @update:modelValue="
                      toggleCampaignStatus(
                        slotProps.data
                      )
                      " class="pa-2" />

                  <span :class="slotProps.data.is_active
                      ? 'status-active'
                      : 'status-inactive'
                    ">
                    {{
                      slotProps.data.is_active
                        ? 'Active'
                        : 'Inactive'
                    }}
                  </span>

                </div>

              </template>

            </Column>


            <!-- Actions -->
            <Column header="" style="width: 100px; border-radius: 10px;">

              <template #body="slotProps">

                <v-menu>

                  <template #activator="{ props }">

                    <v-btn icon v-bind="props" variant="text">
                      <v-icon>
                        mdi-dots-horizontal
                      </v-icon>
                    </v-btn>

                  </template>

                  <v-list>

                    <v-list-item @click="
                      openEditModal(
                        slotProps.data
                      )
                      ">

                      <template #prepend>
                        <v-icon>
                          mdi-pencil-outline
                        </v-icon>
                      </template>

                      Edit Campaign

                    </v-list-item>

                    <v-list-item @click="viewCampaignReferrals(slotProps.data)">
                      <template #prepend>
                        <v-icon>
                          mdi-account-multiple-outline
                        </v-icon>
                      </template>

                      View Referrers
                    </v-list-item>

                  </v-list>

                </v-menu>

              </template>

            </Column>

          </DataTable>

        </v-card>


        <!-- CREATE / EDIT CAMPAIGN -->
        <v-dialog v-model="campaignDialog" max-width="700">

          <v-card class="pa-6">

            <v-card-title class="text-h6 mb-4 text-center">

              {{
                isEditing
                  ? 'Edit Campaign'
                  : 'Create Campaign'
              }}

            </v-card-title>


            <form @submit.prevent="handleSaveCampaign" class="space-y-5">

              <!-- Campaign Name -->
              <div class="flex flex-col gap-2">

                <label class="text-sm font-medium">
                  Campaign Name
                </label>

                <v-text-field v-model="campaignForm.name" variant="outlined" placeholder="Enter campaign name" :rules="[
                  v => !!v || 'Campaign name is required',
                  v =>
                    (v && v.length <= 255) ||
                    'Maximum 255 characters'
                ]" />

              </div>


              <!-- Description -->
              <div class="flex flex-col gap-2">

                <label class="text-sm font-medium">
                  Description
                </label>

                <v-textarea v-model="campaignForm.description" variant="outlined" rows="3"
                  placeholder="Enter campaign description" />

              </div>


              <!-- Campaign Type -->
              <div class="flex flex-col gap-2">

                <label class="text-sm font-medium">
                  Campaign Type
                </label>

                <v-select v-model="campaignForm.type" :items="campaignTypes" item-title="title" item-value="value"
                  variant="outlined" placeholder="Select campaign type" />

              </div>


              <!-- Reward Type -->
              <div class="flex flex-col gap-2">

                <label class="text-sm font-medium">
                  Reward Type
                </label>

                <v-select v-model="campaignForm.reward_type" :items="rewardTypes" item-title="title" item-value="value"
                  variant="outlined" placeholder="Select reward type" />

              </div>


              <!-- Fixed Reward -->
              <div v-if="
                campaignForm.reward_type === 'fixed'
              " class="flex flex-col gap-2">

                <label class="text-sm font-medium">
                  Reward Amount
                </label>

                <v-text-field v-model="campaignForm.reward_amount" type="number" variant="outlined" prefix="₦"
                  placeholder="Enter reward amount" />

              </div>


              <!-- Percentage Reward -->
              <div v-if="
                campaignForm.reward_type === 'percentage'
              " class="flex flex-col gap-2">

                <label class="text-sm font-medium">
                  Reward Percentage
                </label>

                <v-text-field v-model="campaignForm.reward_percentage
                  " type="number" variant="outlined" suffix="%" placeholder="Enter percentage" />

              </div>


              <!-- START & END DATE -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div class="flex flex-col gap-2">

                  <label class="text-sm font-medium">
                    Start Date
                  </label>

                  <v-text-field v-model="campaignForm.starts_at" type="datetime-local" variant="outlined" />

                </div>


                <div class="flex flex-col gap-2">

                  <label class="text-sm font-medium">
                    End Date
                  </label>

                  <v-text-field v-model="campaignForm.ends_at" type="datetime-local" variant="outlined" />

                </div>

              </div>


              <!-- Active -->
              <div class="campaign-active-wrapper">

                <v-switch v-model="campaignForm.is_active" color="success" hide-details />

                <div>

                  <div class="font-medium">
                    Campaign Status
                  </div>

                  <div class="text-sm text-gray-500">

                    {{
                      campaignForm.is_active
                        ? 'Campaign is Active'
                        : 'Campaign is Inactive'
                    }}

                  </div>

                </div>

              </div>


              <!-- ACTIONS -->
              <div class="flex justify-center align-center gap-3 pt-4" style="
                  display:flex;
                  justify-content:center;
                  align-items:center;
                  gap:12px;
                  padding-top:16px;
                ">

                <v-btn variant="text" @click="campaignDialog = false">
                  Cancel
                </v-btn>

                <v-btn color="black" type="submit" :loading="campaignLoading" :disabled="!isCampaignFormValid">

                  {{
                    isEditing
                      ? 'Update Campaign'
                      : 'Save Campaign'
                  }}

                </v-btn>

              </div>

            </form>

          </v-card>

        </v-dialog>


        <!-- Snackbar -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" location="top right">

          {{ snackbar.message }}

          <template #actions>

            <v-btn variant="outlined" @click="snackbar.show = false">
              Cancel
            </v-btn>

          </template>

        </v-snackbar>

      </v-container>

    </v-main>
  </v-app>
</template>


<script setup>

import { ref, computed, onMounted, onUnmounted } from "vue"
import AdminSidebar from "@/components/AdminSidebar.vue"
import Header from "@/components/Header.vue"
import { campaignStore } from "@/store/campaign"
import { formatDate, getAllErrors } from "@/util"
import { useRouter } from "vue-router"
import { executeSaveCampaign, executeUpdateCampaign, executeToggleCampaign } from "@/api/campaign"

const camp = campaignStore()
const router = useRouter()

/* --------------------------------------------------
   SIDEBAR
-------------------------------------------------- */

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)


const toggleSidebar = () => {

  if (isMobile.value) {

    drawerOpen.value =
      !drawerOpen.value

  } else {

    isSidebarCollapsed.value =
      !isSidebarCollapsed.value

  }

}


const checkMobile = () => {

  isMobile.value =
    window.innerWidth < 768

  drawerOpen.value =
    !isMobile.value

}


/* --------------------------------------------------
   TABLE
-------------------------------------------------- */

const filters = ref({
  global: {
    value: null,
    matchMode: "contains"
  }
})


const campaigns = computed(
  () => camp.campaigns || []
)


const clearFilters = () => {

  filters.value = {
    global: {
      value: null,
      matchMode: "contains"
    }
  }

}


/* --------------------------------------------------
   DIALOG
-------------------------------------------------- */

const campaignDialog = ref(false)

const campaignLoading = ref(false)

const statusLoadingId = ref(null)

const isEditing = ref(false)

const editingId = ref(null)


/* --------------------------------------------------
   CAMPAIGN TYPES
-------------------------------------------------- */

const campaignTypes = [

  {
    title: "Referral",
    value: "referral"
  },

  {
    title: "Partner",
    value: "partner"
  },

  {
    title: "General",
    value: "general"
  },

  {
    title: "Bonus",
    value: "bonus"
  },

  {
    title: "Seasonal",
    value: "seasonal"
  },

  {
    title: "Special",
    value: "special"
  },

  {
    title: "Loyalty",
    value: "loyalty"
  },

  {
    title: "Promotional",
    value: "promotional"
  },

  {
    title: "Affiliate",
    value: "affiliate"
  },

  {
    title: "Influencer",
    value: "influencer"
  },

  {
    title: "Event",
    value: "event"
  },

  {
    title: "Contest",
    value: "contest"
  },

  {
    title: "Survey",
    value: "survey"
  },

  {
    title: "Feedback",
    value: "feedback"
  },

  {
    title: "Welcome",
    value: "welcome"
  },

  {
    title: "Holiday",
    value: "holiday"
  },

  {
    title: "Anniversary",
    value: "anniversary"
  },

  {
    title: "Exclusive",
    value: "exclusive"
  }

]


/* --------------------------------------------------
   REWARD TYPES
-------------------------------------------------- */

const rewardTypes = [

  {
    title: "Fixed Amount",
    value: "fixed"
  },

  {
    title: "Percentage",
    value: "percentage"
  }

]


/* --------------------------------------------------
   FORM
-------------------------------------------------- */

const campaignForm = ref({

  name: "",

  description: "",

  type: "referral",

  reward_type: "fixed",

  reward_amount: null,

  reward_percentage: null,

  starts_at: "",

  ends_at: "",

  is_active: true

})


/* --------------------------------------------------
   FORM VALIDATION
-------------------------------------------------- */

const isCampaignFormValid = computed(() => {

  if (!campaignForm.value.name)
    return false

  if (!campaignForm.value.type)
    return false

  if (!campaignForm.value.reward_type)
    return false

  if (!campaignForm.value.starts_at)
    return false

  if (!campaignForm.value.ends_at)
    return false


  if (
    campaignForm.value.reward_type === "fixed"
    &&
    !campaignForm.value.reward_amount
  ) {

    return false

  }


  if (
    campaignForm.value.reward_type === "percentage"
    &&
    !campaignForm.value.reward_percentage
  ) {

    return false

  }


  return true

})


/* --------------------------------------------------
   RESET
-------------------------------------------------- */

const resetForm = () => {

  campaignForm.value = {

    name: "",

    description: "",

    type: "referral",

    reward_type: "fixed",

    reward_amount: null,

    reward_percentage: null,

    starts_at: "",

    ends_at: "",

    is_active: true

  }

}


/* --------------------------------------------------
   EDIT
-------------------------------------------------- */

const openEditModal = (item) => {

  isEditing.value = true

  editingId.value = item.uuid


  campaignForm.value = {

    name: item.name || "",

    description:
      item.description || "",

    type:
      item.type || "referral",

    reward_type:
      item.reward_type || "fixed",

    reward_amount:
      item.reward_amount ?? null,

    reward_percentage:
      item.reward_percentage ?? null,

    starts_at:
      item.starts_at
        ? item.starts_at
          .replace(" ", "T")
          .slice(0, 16)
        : "",

    ends_at:
      item.ends_at
        ? item.ends_at
          .replace(" ", "T")
          .slice(0, 16)
        : "",

    is_active:
      Boolean(item.is_active)

  }


  campaignDialog.value = true

}


/* --------------------------------------------------
   CREATE / UPDATE
-------------------------------------------------- */

const handleSaveCampaign = async () => {

  campaignLoading.value = true

  try {

    const payload = new FormData()


    payload.append("name", campaignForm.value.name)
    payload.append("description", campaignForm.value.description ?? "")
    payload.append("type", campaignForm.value.type)
    payload.append("reward_type", campaignForm.value.reward_type)
    payload.append("reward_amount", campaignForm.value.reward_type === "fixed" ? campaignForm.value.reward_amount ?? "" : "")
    payload.append("reward_percentage", campaignForm.value.reward_type === "percentage" ? campaignForm.value.reward_percentage ?? "" : "")
    payload.append("starts_at", campaignForm.value.starts_at ?? "")
    payload.append("ends_at", campaignForm.value.ends_at ?? "")
    payload.append("is_active", campaignForm.value.is_active ? "1" : "0")

    if (isEditing.value) {

      await executeUpdateCampaign(
        editingId.value,
        payload
      )
      showSnackbar(
        "Campaign updated successfully"
      )
    } else {
      await executeSaveCampaign(
        payload
      )
      showSnackbar(
        "Campaign created successfully"
      )

    }

    campaignDialog.value = false

    await camp.loadCampaigns()

    resetForm()

  } catch (error) {

    console.error(error)

    const msg =
      getAllErrors(error)

    showSnackbar(
      msg || "Campaign save error",
      "error"
    )

  } finally {

    campaignLoading.value = false

  }

}


/* --------------------------------------------------
   TOGGLE STATUS
-------------------------------------------------- */

const toggleCampaignStatus = async (campaign) => {

  const previousStatus = !campaign.is_active

  statusLoadingId.value = campaign.uuid
  try {

    await executeToggleCampaign(
      campaign.uuid
    )

    showSnackbar(
      campaign.is_active
        ? "Campaign activated successfully"
        : "Campaign deactivated successfully"
    )

  } catch (error) {

    // Restore previous value
    campaign.is_active =
      previousStatus

    console.error(error)

    showSnackbar(
      getAllErrors(error) ||
      "Unable to update campaign status",
      "error"
    )

  } finally {

    statusLoadingId.value = null

  }

}


/* --------------------------------------------------
   HELPERS
-------------------------------------------------- */

const formatCampaignType = (type) => {

  if (!type)
    return "-"

  return type
    .replaceAll("_", " ")
    .replace(
      /\b\w/g,
      char => char.toUpperCase()
    )

}


const formatAmount = (amount) => {

  if (
    amount === null ||
    amount === undefined
  ) {

    return "0.00"

  }

  return Number(amount)
    .toLocaleString(
      "en-NG",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    )

}


/* --------------------------------------------------
   SNACKBAR
-------------------------------------------------- */

const snackbar = ref({

  show: false,

  message: "",

  color: "success"

})


const showSnackbar = (
  message,
  color = "success"
) => {

  snackbar.value.message =
    message

  snackbar.value.color =
    color

  snackbar.value.show =
    true

}


/* --------------------------------------------------
   LIFECYCLE
-------------------------------------------------- */

onMounted(async () => {

  checkMobile()

  window.addEventListener(
    "resize",
    checkMobile
  )

  try {

    await camp.loadCampaigns()

  } catch (error) {

    console.error(error)

  }

})


onUnmounted(() => {

  window.removeEventListener(
    "resize",
    checkMobile
  )

})

const viewCampaignReferrals = (campaign) => {
  router.push({
    name: "referrers",
    params: {
      uuid: campaign.uuid
    }
  })
}

</script>


<style scoped>
.main {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  background: #f8fafc;
}


/* CARD */

.table-card {
  padding: 20px;
  border-radius: 14px;
  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.05);
}


/* HEADER */

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}


/* TABLE */

.modern-table {
  border: none;
}


/* CAMPAIGN */

.campaign-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.campaign-image-wrapper {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}

.campaign-image {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
}

.campaign-image-placeholder {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #eef7f0;
  color: #065f46;
  display: flex;
  align-items: center;
  justify-content: center;
}

.campaign-name {
  font-weight: 700;
}

.campaign-description {
  font-size: 13px;
  color: #64748b;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


/* TYPE */

.type-pill {
  padding: 5px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
}


/* REFERRALS */

.referral-count {
  font-weight: 700;
  color: #065f46;
}


/* STATUS */

.status-toggle {
  display: flex;
  align-items: center;
  gap: 5px;
}

.status-active {
  color: #16a34a;
  font-size: 12px;
  font-weight: 600;
}

.status-inactive {
  color: #dc2626;
  font-size: 12px;
  font-weight: 600;
}


/* FORM STATUS */

.campaign-active-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  background: #f8fafc;
}


/* BUTTON */

.theme-button {
  height: 46px;
  background: #000;
  color: #fff;
  border-radius: 5px;
  font-weight: 600;
  transition: all 0.25s ease;
  margin: 0;
}

.theme-button:hover {
  background: #292828;
}

.theme-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}


/* DATATABLE */

:deep(.modern-table .p-datatable-thead > tr > th) {
  background: #eef7f0;
  color: #065f46;
  font-weight: 600;
  border-bottom: 2px solid #c1edc3;
}

.p-datatable-tbody>tr {
  cursor: pointer;
}

.p-datatable-tbody>tr:hover {
  background-color: #f5f5f5;
}
</style>